import React from "react";
import { connect } from "react-redux";
import Infinite from "react-infinite";
import { withContentRect } from "react-measure";
import { PropTypes } from "prop-types";
import { OrderedMap } from "immutable";
import classNames from "classnames";
import { Helmet } from "react-helmet";

import {
  getTrendingTopicsAsync,
  getTagsNextPageAsync,
  getTagsAsync,
  resetTagState,
} from "settings/actions/tags";
import TrendingTopicsRow from "settings/components/TrendingTopicsRow";
import SettingsWrapper from "settings/components/SettingsWrapper";
import SettingsHeader from "settings/components/SettingsHeader";
import Search from "settings/components/Search";
import Spinner from "components/library/Spinner";
import NoResults from "components/library/NoResults";

import styles from "./TrendingTopics.css";

const INITIAL_TAG_ROW_HEIGHT = 70;

const MeasuredTrendingTopicsRow = withContentRect("client")(props => (
  <div ref={props.measureRef} key={props.key}>
    <TrendingTopicsRow {...props} />
  </div>
));

class TrendingTopics extends React.Component {
  constructor(props) {
    super(props);

    this.toggleSelection = this._toggleSelection.bind(this);
    this.handleInfiniteLoad = this._handleInfiniteLoad.bind(this);
    this.handleSearch = this._handleSearch.bind(this);

    const filteredTags = this.filteredTagsWithoutTrending(props);

    this.state = {
      selected: {},
      infiniteLoading: false,
      disableMore: false,
      elementHeights: Array.apply(null, Array(filteredTags.size)).map(
        () => INITIAL_TAG_ROW_HEIGHT
      ),
      filteredTags: filteredTags,
    };
  }

  componentDidMount() {
    this.props.resetTagState();
    this.props.getTrendingTopicsAsync();
    this.props.getTagsAsync({ ordering: "-recent_stories,name" });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.tags !== this.props.tags) {
      const filteredTags = this.filteredTagsWithoutTrending(nextProps);
      this.setState({
        filteredTags: filteredTags,
        // Because children haven't been measured yet, set height initially to an approximate value: 70px
        elementHeights: Array.apply(null, Array(filteredTags.size)).map(
          () => INITIAL_TAG_ROW_HEIGHT
        ),
        edgeOffset: INITIAL_TAG_ROW_HEIGHT * 2,
      });
    }
  }

  _handleSearch(query) {
    this.props.getTagsAsync({ ordering: "-recent_stories,name", name: query });
  }

  _handleInfiniteLoad() {
    this.setState({ infiniteLoading: true });
    return this.props
      .getTagsNextPageAsync()
      .catch(() => this.setState({ edgeOffset: undefined }))
      .then(() => this.setState({ infiniteLoading: false }));
  }

  _toggleSelection(id) {
    this.setState({
      selected: {
        ...this.state.selected,
        [id]: !this.state.selected[id],
      },
    });
  }

  isTagSelected(tag) {
    return this.state.selected[tag.id] || false;
  }

  filteredTagsWithoutTrending(props) {
    const trendingIds = this.props.trendingTopics.keySeq().toArray();
    return props.tags.filter(tag => !trendingIds.includes(tag.id));
  }

  isATrendingTopicSelected() {
    return false;
    // return Object.values(this.props.trendingTopics).filter((tag) => (this.state.selected[tag.id])).length > 0;
  }

  updateSize(index, height) {
    const heights = [...this.state.elementHeights];
    heights[index] = height;
    this.setState({ elementHeights: heights });
  }

  render() {
    const disableMore = this.isATrendingTopicSelected();
    let trendingTopicsArray = [];
    let filteredTagsArray = [];

    if (this.props.trendingTopics) {
      // Search trending topics locally, instead of via API call
      trendingTopicsArray = this.props.trendingTopics.toArray().filter(tag => {
        return tag.name
          .toLowerCase()
          .includes((this.props.filter.name || "").toLowerCase());
      });
    }

    if (this.state.filteredTags) {
      filteredTagsArray = this.state.filteredTags.toArray();
    }

    const noResults = (
      <div className={styles.noTags}>
        <NoResults message={"No tags"} />
      </div>
    );

    let lists =
      !trendingTopicsArray.length && !filteredTagsArray.length ? (
        noResults
      ) : (
        <div>
          {trendingTopicsArray.length > 0 && (
            <div className={styles.listSection}>
              <p className={styles.listLabel}>Trending Topics</p>
              <ol className={styles.list}>
                {trendingTopicsArray.map((tag, i) => (
                  <TrendingTopicsRow
                    tag={tag}
                    key={tag.id}
                    index={i}
                    trending={true}
                    onChange={() => this.toggleSelection(tag.id)}
                    selected={this.isTagSelected(tag)}
                  />
                ))}
              </ol>
            </div>
          )}
          {filteredTagsArray.length > 0 && (
            <div className={styles.listSection}>
              <p className={styles.listLabel}>More Topics</p>
              <ol className={styles.list}>
                <Infinite
                  useWindowAsScrollContainer
                  elementHeight={this.state.elementHeights}
                  infiniteLoadBeginEdgeOffset={this.state.edgeOffset}
                  loadingSpinnerDelegate={
                    <div className={styles.spinnerContainer}>
                      <Spinner />
                    </div>
                  }
                  onInfiniteLoad={this.handleInfiniteLoad}
                  isInfiniteLoading={this.state.infiniteLoading}
                >
                  {filteredTagsArray.map((tag, i) => {
                    return (
                      <MeasuredTrendingTopicsRow
                        tag={tag}
                        key={tag.id}
                        disabled={disableMore}
                        selected={this.isTagSelected(tag)}
                        onChange={() => this.toggleSelection(tag.id)}
                        onResize={contentRect =>
                          this.updateSize(i, contentRect.client.height)
                        }
                      />
                    );
                  })}
                </Infinite>
              </ol>
            </div>
          )}
        </div>
      );

    return (
      <SettingsWrapper>
        <Helmet>
          <title>Trending topics</title>
        </Helmet>

        <SettingsHeader
          title="Trending topics"
          description="Trending topics are dynamically updated based on the amount of stories published with a given primary tag in a 48 hour period."
        >
          <Search placeholder="Find a topic" onChange={this.handleSearch} />
        </SettingsHeader>

        {this.props.isLoadingTags || this.props.isLoadingTrendingTopics ? (
          <div className={classNames(styles.noTags, styles.spinnerContainer)}>
            <Spinner />
          </div>
        ) : (
          lists
        )}
      </SettingsWrapper>
    );
  }
}

const mapStateToProps = state => ({
  tags: state.settings.tags.tags,
  trendingTopics: state.settings.tags.trendingTopics,
  isLoadingTrendingTopics: state.settings.tags.isLoadingTrendingTopics,
  isLoadingTags: state.settings.tags.isLoadingTags,
  filter: state.settings.tags.meta.filter,
});

const mapDispatchToProps = {
  getTrendingTopicsAsync,
  getTagsAsync,
  getTagsNextPageAsync,
  resetTagState,
};

TrendingTopics.propTypes = {
  getTrendingTopicsAsync: PropTypes.func.isRequired,
  getTagsAsync: PropTypes.func.isRequired,
  resetTagState: PropTypes.func.isRequired,
  getTagsNextPageAsync: PropTypes.func.isRequired,
  tags: PropTypes.instanceOf(OrderedMap),
  trendingTopics: PropTypes.instanceOf(OrderedMap),
  isLoadingTrendingTopics: PropTypes.bool.isRequired,
  isLoadingTags: PropTypes.bool.isRequired,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TrendingTopics);
